<!--

    This component will generate a grid in which the items are displayed. It will adjust the grid dynamicaly acording to the
    screen size. //TODO 02.08.2023 should be improved


    There are no headers in this ViewMode, but it will show a sorting option in the right top corner. The configuration happen
    via FilterOption.


    Item component:
        - 

-->

<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { fade } from 'svelte/transition';
	import { clickOutside } from '../../../helper/Extensions';
	import Icon from '../../Icon.svelte';
	import {
		SortingOrder,
		type FilterOption,
		type ListItemInfo,
		type MultiSelectionItem,
		type SimpleListStore,
		type StoreListener
	} from '../../../types/ListTypes';
	import DefaultEmptyListPlaceholder from './DefaultEmptyListPlaceholder.svelte';
	import DefaultSimpleListFilterContainer from './DefaultSimpleListFilterContainer.svelte';

	interface Props {
		id?: string | null;
		store: SimpleListStore;
		itemComponent: any;
		selectedItemCount: number;
		activeSorting: FilterOption | null;
		onSelection: (info: ListItemInfo) => void;
		onDblClick: (item: MultiSelectionItem) => void;
		onChangeSortOrder: (header: FilterOption) => void;
		onRequestContextmenu: (info: ListItemInfo) => void;
		onDragStartRequest: (info: ListItemInfo) => void;
		onDrop: () => void;
		onAddRequest: (index: number) => void;
		onChildDeleted?: (item: any) => void;
		onCustomEvent: (e: any) => void;
		onEditRequest?: (item: MultiSelectionItem) => void;
	}

	let {
		id = null,
		store = $bindable(),
		itemComponent,
		selectedItemCount,
		activeSorting,
		onSelection,
		onDblClick,
		onChangeSortOrder,
		onRequestContextmenu,
		onDragStartRequest,
		onDrop,
		onAddRequest,
		onCustomEvent,
		onChildDeleted = undefined,
		onEditRequest = undefined
	}: Props = $props();

	//Should the sorting options be shown? This is the internal use if the dropdown should be shown.
	let showSortings: boolean = $state(false);
	//Should the filter slot be shown? This is internal use and will be set to true if you click on the filter icon button.
	let showFilter: boolean = false;

	let scrollContainer: HTMLElement | undefined = $state();

	let scrollListener: HTMLElement | undefined = $state();
	let observer: IntersectionObserver;
	let loading: boolean = $state(false);
	let loopCount: number = $state(0);

	let storeListener: StoreListener = {
		onFilterApplied: () => {
			//If the scroll listener is not yet bound do not remove the observer
			if (scrollListener) {
				//We have to remove the listener otherwise the event may not be send again
				if (observer) {
					observer.disconnect();
					//Add it back again
					observer.observe(scrollListener);
				}
			}
		}
	};

	async function requestLoadMore(element: HTMLElement) {
		if (loading) return;
		loading = true;

		const total = store.data.length + store.listFilter.deletedCounter;
		const size = store.listFilter.size || 0;
		const pageSizeOk = total % size === 0;
		const nextPagePossible = store.listFilter.page * size <= total;

		//Check if we should actually load more.
		if (!pageSizeOk || !nextPagePossible) {
			loading = false;
			return;
		}

		//Make the effective load call
		await store.loadData();

		loading = false;

		// Check if marker is still visible (screen too big)
		if (
			scrollContainer &&
			element.getBoundingClientRect().top < scrollContainer.getBoundingClientRect().bottom
		) {
			//Simple loop protection
			if (loopCount < 7) {
				loopCount += 1;
				//If it is try to load more
				await requestLoadMore(element);
				//Reset the loop count after
				loopCount = 0;
			}
		}
	}

	function setupObserver(element: HTMLElement) {
		observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					requestLoadMore(element);
				}
			},
			{
				root: scrollContainer,
				rootMargin: '20px',
				threshold: 0
			}
		);

		observer.observe(element);
	}

	onMount(() => {
		store.addNotifier(storeListener);
	});

	onDestroy(() => {
		//Remove the listener again
		store.removeNotifier(storeListener);

		if (observer) {
			observer.disconnect();
		}
	});
</script>

<DefaultSimpleListFilterContainer
	bind:simpleListConfiguration={store.listConfig}
	bind:listFilter={store.listFilter}
	{showFilter}
/>

<div class="px-8 py-4 flex justify-end bg-background dark:bg-dark-200 rounded-t-md">
	<!--Placeholder-->
	<div></div>

	<!--Sorting-->
	{#if !showFilter}
		{#if activeSorting}
			<div in:fade class="flex items-center gap-2 justify-end relative">
				<button
					onclick={() => (showSortings = !showSortings)}
					class="col-start-2 px-2 py-1 text-sm font-bold uppercase text-secondary-200 border-b-2 border-secondary-200 dark:text-secondary-500 dark:border-secondary-500"
				>
					{activeSorting.label}
				</button>
				<button
					onclick={() => {
						onChangeSortOrder(activeSorting);
					}}
				>
					{#if store.listFilter.sortingOrder === SortingOrder.ASC}
						<Icon
							size="big"
							name="caret-up"
							classInfo="fill-secondary-300 dark:fill-secondary-500"
						/>
					{/if}
					{#if store.listFilter.sortingOrder === SortingOrder.DESC}
						<Icon
							size="big"
							name="caret-down"
							classInfo="fill-secondary-300 dark:fill-secondary-500"
						/>
					{/if}
				</button>

				<!--Possible options-->
				{#if showSortings}
					<div
						use:clickOutside={() => (showSortings = false)}
						class="grid p-2 gap-2 absolute bg-white dark:bg-dark-300 top-[80%] right-10 z-40 rounded-[5px] shadow-xl border border-gray"
					>
						{#each store.listFilter.filterOptions as sorting, index}
							{#if sorting.isSorting != null ? sorting.isSorting : true}
								<button
									class="p-2 hover:text-secondary-200 dark:hover:text-secondary-500"
									onclick={() => {
										//Hide the options menu
										showSortings = false;
										//Change the sorting
										onChangeSortOrder(sorting);
									}}
								>
									<p>
										{sorting.label}
									</p>
								</button>
								{#if index < store.listFilter.filterOptions.length - 1}
									<div class="w-full border-b border-gray"></div>
								{/if}
							{/if}
						{/each}
					</div>
				{/if}
			</div>
		{/if}
	{/if}
</div>

<!--Data part-->
<div
	class="grow overflow-y-auto bg-background dark:bg-dark-200"
	name="list-container"
	bind:this={scrollContainer}
>
	<!--Show place holder if data is empty-->
	{#if store.data.length <= 0}
		<div class="p-2">
			<DefaultEmptyListPlaceholder
				onAddRequest={(index) => {
					onAddRequest?.(index);
				}}
				showAdd={store.listConfig.showEmptyAdd}
			/>
		</div>
	{:else}
		<!--Container for items, do not remove the item-list class, it is used to scroll from the outside.-->
		<div
			id={store.listFilter.localStorageKey ? store.listFilter.localStorageKey : id}
			class:gap-0={store.listConfig.cardModeConfig.gapSize == 0}
			class:gap-2={store.listConfig.cardModeConfig.gapSize == 2}
			class:gap-4={store.listConfig.cardModeConfig.gapSize == 4}
			class:gap-6={store.listConfig.cardModeConfig.gapSize == 6}
			class:xl:grid-cols-2={store.listConfig.cardModeConfig.amountOfCols == 2}
			class:md:grid-cols-2={store.listConfig.cardModeConfig.amountOfCols == 4 ||
				store.listConfig.cardModeConfig.amountOfCols == 5}
			class:xl:grid-cols-4={store.listConfig.cardModeConfig.amountOfCols == 4}
			class:xl:grid-cols-5={store.listConfig.cardModeConfig.amountOfCols == 5}
			class:sm:grid-cols-2={store.listConfig.cardModeConfig.amountOfCols == 6}
			class:md:grid-cols-4={store.listConfig.cardModeConfig.amountOfCols == 6}
			class:xl:grid-cols-6={store.listConfig.cardModeConfig.amountOfCols == 6}
			class="grid px-8 pb-8 content-start overflow-y-auto sm:grid-cols-1 item-list"
			class:pt-4={store.listFilter.filterOptions.length === 0}
		>
			{#each store.data as item, index}
				{@const SvelteComponent = itemComponent}
				<SvelteComponent
					{store}
					onChildDeleted={() => {
						store.listFilter.deletedCounter += 1;
						onChildDeleted?.(item);
					}}
					onEditRequest={() => {
						onEditRequest?.(item);
					}}
					onClick={(e: MouseEvent) => {
						onSelection({
							item: item,
							index: index,
							event: e
						});
					}}
					{onCustomEvent}
					onDblClick={(e: MouseEvent) => {
						onDblClick(item);
					}}
					onRequestContextmenu={(e: MouseEvent) => {
						onRequestContextmenu({
							item: item,
							index: index,
							event: e
						});
					}}
					onDragStartRequest={(event: DragEvent) => {
						onDragStartRequest({
							item: item,
							index: index,
							event: event
						});
					}}
					{onDrop}
					{item}
					{index}
					filterOptions={store.listFilter.filterOptions}
					viewMode={store.listConfig.viewMode}
					selected={item.selected}
					{selectedItemCount}
				/>
			{/each}
		</div>

		{#if store.listFilter.size || 0 > 0}
			<div bind:this={scrollListener} class="h-2" use:setupObserver></div>
		{/if}
	{/if}
</div>
